import { useState, useEffect } from "react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { AdminNavigation } from "@/components/AdminNavigation";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { BarChart3, Activity, Target, Eye, Save, Info, FileText, CalendarDays } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

interface TrackingSettings {
  ga4MeasurementId: string;
  ga4Enabled: boolean;
  fbPixelId: string;
  fbPixelEnabled: boolean;
  performanceMonitoring: boolean;
}

const defaultSettings: TrackingSettings = {
  ga4MeasurementId: "",
  ga4Enabled: false,
  fbPixelId: "",
  fbPixelEnabled: false,
  performanceMonitoring: true,
};

const TrackingSettings = () => {
  const { toast } = useToast();
  const [settings, setSettings] = useState<TrackingSettings>(defaultSettings);

  useEffect(() => {
    let active = true;
    supabase
      .from("tracking_settings")
      .select("ga4_measurement_id, ga4_enabled, fb_pixel_id, fb_pixel_enabled, performance_monitoring")
      .eq("singleton_key", true)
      .single()
      .then(({ data, error }) => {
        if (!active) return;
        if (error) {
          toast({ title: "Fehler beim Laden", description: "Die Tracking-Einstellungen konnten nicht geladen werden.", variant: "destructive" });
          return;
        }
        setSettings({
          ga4MeasurementId: data.ga4_measurement_id ?? "",
          ga4Enabled: data.ga4_enabled,
          fbPixelId: data.fb_pixel_id ?? "",
          fbPixelEnabled: data.fb_pixel_enabled,
          performanceMonitoring: data.performance_monitoring,
        });
      });
    return () => { active = false; };
  }, [toast]);

  const handleSave = async () => {
    // Validate GA4 ID format
    if (settings.ga4Enabled && settings.ga4MeasurementId) {
      if (!settings.ga4MeasurementId.match(/^G-[A-Z0-9]+$/)) {
        toast({
          title: "Ungültige GA4 ID",
          description: "Google Analytics 4 Measurement ID muss mit 'G-' beginnen (z.B. G-XXXXXXXXXX)",
          variant: "destructive",
        });
        return;
      }
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast({ title: "Nicht angemeldet", description: "Bitte melden Sie sich erneut an.", variant: "destructive" });
      return;
    }

    const { error } = await supabase
      .from("tracking_settings")
      .update({
        ga4_measurement_id: settings.ga4MeasurementId.trim() || null,
        ga4_enabled: settings.ga4Enabled,
        fb_pixel_id: settings.fbPixelId.trim() || null,
        fb_pixel_enabled: settings.fbPixelEnabled,
        performance_monitoring: settings.performanceMonitoring,
        updated_by: user.id,
      })
      .eq("singleton_key", true);

    if (error) {
      toast({ title: "Speichern fehlgeschlagen", description: "Die Tracking-Einstellungen konnten nicht aktualisiert werden.", variant: "destructive" });
      return;
    }

    // Initialize tracking scripts
    if (settings.ga4Enabled && settings.ga4MeasurementId) {
      initializeGA4(settings.ga4MeasurementId);
    }

    if (settings.fbPixelEnabled && settings.fbPixelId) {
      initializeFacebookPixel(settings.fbPixelId);
    }

    toast({
      title: "Einstellungen gespeichert",
      description: "Tracking-Einstellungen wurden erfolgreich aktualisiert.",
    });

    // Reload page to apply changes
    setTimeout(() => window.location.reload(), 1500);
  };

  const initializeGA4 = (measurementId: string) => {
    // Check if cookie consent is given
    const consent = localStorage.getItem("cookie-consent");
    if (consent !== "accepted") return;

    // Remove existing GA script
    const existingScript = document.querySelector(`script[src*="googletagmanager.com/gtag/js"]`);
    if (existingScript) existingScript.remove();

    // Add GA4 script
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    function gtag(...args: any[]) {
      window.dataLayer.push(args);
    }
    gtag("js", new Date());
    gtag("config", measurementId, {
      anonymize_ip: true,
      cookie_flags: "SameSite=None;Secure",
    });

    (window as any).gtag = gtag;
  };

  const initializeFacebookPixel = (pixelId: string) => {
    // Check if cookie consent is given
    const consent = localStorage.getItem("cookie-consent");
    if (consent !== "accepted") return;

    (window as any).fbq = (window as any).fbq || function() {
      ((window as any).fbq.q = (window as any).fbq.q || []).push(arguments);
    };
    (window as any)._fbq = (window as any)._fbq || (window as any).fbq;
    (window as any).fbq.loaded = true;

    const script = document.createElement("script");
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(script);

    (window as any).fbq("init", pixelId);
    (window as any).fbq("track", "PageView");
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <AdminNavigation />
      <main className="pt-24 pb-24">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="break-words text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-2">
                  Tracking & Performance
                </h1>
                <p className="text-xl text-muted-foreground">
                  Verwalten Sie Ihre Marketing- und Performance-Tracking-Tools
                </p>
              </div>
            </div>
          </div>

          <Alert className="mb-8">
            <Info className="h-4 w-4" />
            <AlertDescription>
              Tracking wird nur aktiviert, wenn Besucher den Cookie-Banner akzeptiert haben (DSGVO-konform).
            </AlertDescription>
          </Alert>

          <Tabs defaultValue="analytics" className="w-full">
            <TabsList className="flex h-auto w-full justify-start overflow-x-auto mb-8 p-1 [scrollbar-width:thin]">
              <TabsTrigger value="analytics" className="flex shrink-0 items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Analytics
              </TabsTrigger>
              <TabsTrigger value="marketing" className="flex shrink-0 items-center gap-2">
                <Target className="w-4 h-4" />
                Marketing
              </TabsTrigger>
              <TabsTrigger value="performance" className="flex shrink-0 items-center gap-2">
                <Activity className="w-4 h-4" />
                Performance
              </TabsTrigger>
            </TabsList>

            {/* Google Analytics Tab */}
            <TabsContent value="analytics">
              <Card className="p-4 sm:p-8">
                <div className="space-y-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <h3 className="text-xl font-semibold text-foreground mb-2">
                        Google Analytics 4
                      </h3>
                      <p className="text-muted-foreground">
                        Analysieren Sie Besucherverhalten, Traffic-Quellen und Conversions
                      </p>
                    </div>
                    <Switch
                      checked={settings.ga4Enabled}
                      onCheckedChange={(checked) =>
                        setSettings({ ...settings, ga4Enabled: checked })
                      }
                    />
                  </div>

                  {settings.ga4Enabled && (
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="ga4-id">Measurement ID</Label>
                        <Input
                          id="ga4-id"
                          type="text"
                          placeholder="G-XXXXXXXXXX"
                          value={settings.ga4MeasurementId}
                          onChange={(e) =>
                            setSettings({ ...settings, ga4MeasurementId: e.target.value })
                          }
                          className="mt-2"
                        />
                        <p className="text-sm text-muted-foreground mt-2">
                          Finden Sie Ihre Measurement ID in Google Analytics unter Admin → Datenströme
                        </p>
                      </div>

                      <Alert>
                        <Eye className="h-4 w-4" />
                        <AlertDescription>
                          <strong>Setup-Anleitung:</strong>
                          <ol className="list-decimal list-inside mt-2 space-y-1">
                            <li>Gehen Sie zu <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">analytics.google.com</a></li>
                            <li>Erstellen Sie ein neues Property (GA4)</li>
                            <li>Kopieren Sie die Measurement ID (G-XXXXXXXXXX)</li>
                            <li>Fügen Sie sie oben ein und speichern Sie</li>
                          </ol>
                        </AlertDescription>
                      </Alert>
                    </div>
                  )}
                </div>
              </Card>
            </TabsContent>

            {/* Facebook Pixel Tab */}
            <TabsContent value="marketing">
              <Card className="p-4 sm:p-8">
                <div className="space-y-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <h3 className="text-xl font-semibold text-foreground mb-2">
                        Facebook Pixel
                      </h3>
                      <p className="text-muted-foreground">
                        Tracken Sie Conversions und optimieren Sie Facebook-Ads
                      </p>
                    </div>
                    <Switch
                      checked={settings.fbPixelEnabled}
                      onCheckedChange={(checked) =>
                        setSettings({ ...settings, fbPixelEnabled: checked })
                      }
                    />
                  </div>

                  {settings.fbPixelEnabled && (
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="fb-pixel-id">Pixel ID</Label>
                        <Input
                          id="fb-pixel-id"
                          type="text"
                          placeholder="123456789012345"
                          value={settings.fbPixelId}
                          onChange={(e) =>
                            setSettings({ ...settings, fbPixelId: e.target.value })
                          }
                          className="mt-2"
                        />
                        <p className="text-sm text-muted-foreground mt-2">
                          Finden Sie Ihre Pixel ID im Facebook Events Manager
                        </p>
                      </div>

                      <Alert>
                        <Eye className="h-4 w-4" />
                        <AlertDescription>
                          <strong>Setup-Anleitung:</strong>
                          <ol className="list-decimal list-inside mt-2 space-y-1">
                            <li>Gehen Sie zum <a href="https://business.facebook.com/events_manager2" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Facebook Events Manager</a></li>
                            <li>Erstellen Sie einen neuen Pixel</li>
                            <li>Kopieren Sie die Pixel ID (15 Ziffern)</li>
                            <li>Fügen Sie sie oben ein und speichern Sie</li>
                          </ol>
                        </AlertDescription>
                      </Alert>
                    </div>
                  )}
                </div>
              </Card>
            </TabsContent>

            {/* Performance Monitoring Tab */}
            <TabsContent value="performance">
              <Card className="p-4 sm:p-8">
                <div className="space-y-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <h3 className="text-xl font-semibold text-foreground mb-2">
                        Performance Monitoring
                      </h3>
                      <p className="text-muted-foreground">
                        Überwachen Sie Ladezeiten und Core Web Vitals
                      </p>
                    </div>
                    <Switch
                      checked={settings.performanceMonitoring}
                      onCheckedChange={(checked) =>
                        setSettings({ ...settings, performanceMonitoring: checked })
                      }
                    />
                  </div>

                  <Alert>
                    <Activity className="h-4 w-4" />
                    <AlertDescription>
                      Performance-Monitoring erfasst automatisch:
                      <ul className="list-disc list-inside mt-2 space-y-1">
                        <li>Largest Contentful Paint (LCP)</li>
                        <li>First Input Delay (FID)</li>
                        <li>Cumulative Layout Shift (CLS)</li>
                        <li>Time to First Byte (TTFB)</li>
                      </ul>
                      <p className="mt-2">
                        Daten werden in Google Analytics angezeigt (falls aktiviert).
                      </p>
                    </AlertDescription>
                  </Alert>

                  <div className="bg-secondary/30 p-4 rounded-lg">
                    <h4 className="font-semibold text-foreground mb-2">Aktuelle Performance</h4>
                    <p className="text-sm text-muted-foreground">
                      Öffnen Sie die Browser DevTools → Lighthouse, um einen Performance-Report zu erstellen.
                    </p>
                  </div>
                </div>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Save Button */}
          <div className="mt-8 flex justify-end">
            <Button
              size="lg"
              onClick={handleSave}
              className="bg-primary hover:bg-primary-hover text-primary-foreground shadow-glow"
            >
              <Save className="w-5 h-5 mr-2" />
              Einstellungen speichern
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TrackingSettings;
